/**
 * Format binaire d'un ralenti. `video` : images JPEG ; `mesh` : frames du maillage
 * (le joueur a choisi de ne pas montrer sa vidéo : son ralenti non plus).
 *
 * [0] 'R' [1] version [2] type (0 vidéo, 1 maillage) [3..4] n u16
 * puis n × ( t u32 (ms depuis la 1re image) · longueur u32 · octets )
 */
export type ClipKind = 'video' | 'mesh';

export interface ClipFrame {
  readonly t: number;
  readonly data: Uint8Array;
}

export interface Clip {
  readonly kind: ClipKind;
  readonly frames: readonly ClipFrame[];
}

export const MAX_CLIP_BYTES = 1_000_000;
const MAX_FRAMES = 400;
const MAGIC = 0x52;
const VERSION = 1;
const HEADER = 5;
const FRAME_HEADER = 8;
const KINDS: readonly ClipKind[] = ['video', 'mesh'];

export function encodeClip(clip: Clip): Uint8Array {
  if (clip.frames.length > MAX_FRAMES) throw new Error('trop d’images');
  const size = clip.frames.reduce((n, f) => n + FRAME_HEADER + f.data.byteLength, HEADER);
  if (size > MAX_CLIP_BYTES) throw new Error(`ralenti trop gros : ${size} octets`);
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  view.setUint8(0, MAGIC);
  view.setUint8(1, VERSION);
  view.setUint8(2, KINDS.indexOf(clip.kind));
  view.setUint16(3, clip.frames.length, true);
  const t0 = clip.frames[0]?.t ?? 0;
  let offset = HEADER;
  for (const f of clip.frames) {
    view.setUint32(offset, Math.max(0, Math.round(f.t - t0)), true);
    view.setUint32(offset + 4, f.data.byteLength, true);
    out.set(f.data, offset + FRAME_HEADER);
    offset += FRAME_HEADER + f.data.byteLength;
  }
  return out;
}

/** Décode et valide ; `null` si invalide. Ne lève jamais d'exception. */
export function decodeClip(bytes: Uint8Array): Clip | null {
  if (bytes.byteLength < HEADER || bytes.byteLength > MAX_CLIP_BYTES) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint8(0) !== MAGIC || view.getUint8(1) !== VERSION) return null;
  const kind = KINDS[view.getUint8(2)];
  const n = view.getUint16(3, true);
  if (!kind || n > MAX_FRAMES) return null;
  const frames: ClipFrame[] = [];
  let offset = HEADER;
  for (let i = 0; i < n; i++) {
    if (offset + FRAME_HEADER > bytes.byteLength) return null;
    const t = view.getUint32(offset, true);
    const len = view.getUint32(offset + 4, true);
    const start = offset + FRAME_HEADER;
    if (start + len > bytes.byteLength) return null;
    frames.push({ t, data: bytes.slice(start, start + len) });
    offset = start + len;
  }
  return offset === bytes.byteLength ? { kind, frames } : null;
}
